import type { EChartsOption } from 'echarts';

import { buildAnimationConfig } from '../shared/animation';
import { buildTooltipConfig } from '../shared/tooltip';
import { buildComparisonBarLayout, LABEL_INSET } from './layout';
import { buildComparisonBarSeries } from './series';
import { buildComparisonBarTooltipFormatter } from './tooltip';
import type { ComparisonBarChartConfig, ComparisonBarLayout, ComparisonBarRow } from './types';

export type {
  ComparisonBarChartConfig,
  ComparisonBarDeltaDirection,
  ComparisonBarLayout,
  ComparisonBarRange,
  ComparisonBarRow,
  ComparisonDeltaSentiment,
  ComparisonDeltaSentimentColors,
} from './types';
export {
  BAR_LABEL_GAP,
  BAR_STACK_GAP,
  BAR_THICKNESS,
  buildComparisonBarLayout,
  GRID_TOP,
  ROW_PADDING,
  rowHeightFor,
} from './layout';
export { buildComparisonHatchDecal, comparisonHatchCss, COMPARISON_FILL_ALPHA } from './decal';
// Exported so a click handler can tell a real bar from its zero stub, and recover which range was
// clicked, without re-deriving the id scheme.
export { STUB_SERIES_PREFIX, VALUE_SERIES_PREFIX } from './series';

/**
 * The grouped horizontal comparison bar chart.
 *
 * Bars are **magnitudes from a common left origin** — the caller strips the sign — because the
 * comparison being drawn is "how big", and a signed axis would put every expense row's bar on the
 * left of a centre line where its length no longer compares with an income row's.
 *
 * Geometry comes entirely from `buildComparisonBarLayout` and is never recomputed here; the
 * surrounding HTML lays itself out from that same function, and a second copy of the arithmetic is
 * exactly how the two would drift apart.
 */
export const buildComparisonBarChartOption = (
  rows: ComparisonBarRow[],
  config: ComparisonBarChartConfig,
): { option: EChartsOption; layout: ComparisonBarLayout } => {
  const { theme, animated = true, ranges, maxValue, labelWidthPx, deltaColumnPx } = config;

  const layout = buildComparisonBarLayout({
    rowCount: rows.length,
    rangeCount: ranges.length,
    labelWidthPx,
    deltaColumnPx,
    endLabels: rows.flatMap((row) => row.labels),
  });

  const option: EChartsOption = {
    ...buildAnimationConfig(animated),
    animationDurationUpdate: animated ? 300 : 0,
    animationEasingUpdate: 'cubicOut',
    textStyle: { fontFamily: theme.fontFamily },
    grid: {
      left: layout.gridLeft,
      right: layout.gridRight,
      top: layout.gridTop,
      height: layout.gridHeight,
    },
    tooltip: {
      ...buildTooltipConfig(theme),
      trigger: 'axis' as const,
      // Short sections can be lower than the tooltip itself. Mount it outside the chart's
      // height-animating, overflow-hidden viewport so one- and two-row sections do not clip it.
      appendTo: 'body',
      // The categories run down the y-axis, so that is the axis a row hover resolves against.
      // `axis` lives on `axisPointer`, not on `tooltip` itself; the invisible shadow pointer from
      // `buildTooltipConfig` is kept, because the visible row-hover band is drawn in HTML.
      axisPointer: { type: 'shadow' as const, axis: 'y' as const, shadowStyle: { opacity: 0 } },
      formatter: buildComparisonBarTooltipFormatter(rows, config, theme),
    },
    yAxis: {
      type: 'category' as const,
      animation: false,
      inverse: true,
      data: rows.map((row) => row.name),
      axisLabel: {
        color: theme.textColor,
        fontSize: 14,
        lineHeight: 18,
        // Left-aligned so a truncated name lines up under the HTML column header. ECharts
        // right-aligns y-axis labels by default, which would ragged-edge them against it.
        align: 'left' as const,
        width: Math.max(0, labelWidthPx - LABEL_INSET),
        overflow: 'truncate' as const,
        // Positions the label's left edge at the far side of the reserved gutter, since `align`
        // only sets which edge of the text the anchor is.
        margin: labelWidthPx - LABEL_INSET / 2,
      },
      axisLine: { show: false },
      axisTick: { show: false },
      splitLine: { show: false },
    },
    xAxis: {
      type: 'value' as const,
      animation: false,
      // A shared origin and a shared max is what makes bar lengths comparable between rows.
      min: 0,
      max: maxValue,
      axisLabel: { show: false },
      axisLine: { show: false },
      axisTick: { show: false },
      splitLine: { show: false },
    },
    series: buildComparisonBarSeries(rows, config, layout, theme),
  };

  // The layout comes back with the option so the HTML side cannot end up calling the layout
  // function with different inputs than the canvas was built from.
  return { option, layout };
};
